import { Hovercard, Icon, ListItem } from "@resource/atlas";
import {
  atlasRing,
  atlasWarning,
  serviceGreenhouse,
} from "@resource/atlas/icons";
import { useIsZeusJob } from "client/utils/zeus";
import {
  GetOpenJobs_openJobs as Job,
  GetOpenJobs_openJobs_configuredStages as Stage,
  JobRoleGuideTemplateSetupStatus,
} from "generated/schemaTypes";
import { sortBy } from "lodash";
import { ReactNode, useMemo } from "react";
import { UseTableCellProps } from "react-table";
import { formatEntity } from "shared/constants/entities";

function ErrorCell({
  label,
  unconfiguredCount,
}: {
  label: ReactNode;
  unconfiguredCount: number;
}) {
  return (
    <Hovercard.Root placement="bottom-start">
      <Hovercard.Anchor>
        <div className="flex items-center gap-1">
          <span className="text-body-md">{label}</span>
          <Icon
            content={atlasWarning}
            className="text-danger-500 inline"
            size="medium"
          />
        </div>
      </Hovercard.Anchor>
      <Hovercard.Content>
        <>
          {unconfiguredCount
            ? "There are 1 or more stages missing content. Stages with missing content will not be shown to the candidate."
            : `There are no stages in the ${formatEntity(
                "interview plan"
              )}. Jobs must have 1 or more stages added to the ${formatEntity(
                "interview plan"
              )} to create and send guides to candidates.`}
        </>
      </Hovercard.Content>
    </Hovercard.Root>
  );
}

function ValidCell({ label, stages }: { label: ReactNode; stages?: Stage[] }) {
  const stagesOrdered = useMemo(
    () => sortBy(stages ?? [], "position"),
    [stages]
  );
  return stagesOrdered.length ? (
    <Hovercard.Root placement="bottom-start">
      <Hovercard.Anchor>
        <p className="text-body-md">{label}</p>
      </Hovercard.Anchor>
      <Hovercard.Content hasPadding={false}>
        <div className="px-1 py-[0.375rem]">
          <h3 className="text-body-md-heavy px-3 pt-[0.625rem] pb-[0.375rem]">
            ${formatEntity("interview plan", { capitalize: true })}
          </h3>
          {stagesOrdered.map((stage, stageIndex) => (
            <ListItem
              key={stage.id}
              renderContent={({ children }) => (
                <div className="flex gap-2 items-center w-80">
                  <div>
                    <div className="w-5 h-5 relative text-subtle">
                      <Icon className="absolute inset-0" content={atlasRing} />
                      <span className="absolute inset-0 text-body-sm text-[.625rem] text-center w-5 leading-5">
                        {stageIndex + 1}
                      </span>
                    </div>
                  </div>
                  {children}
                </div>
              )}
              trailingContent={
                <Icon content={serviceGreenhouse} size="medium" />
              }
            >
              {stage.name}
            </ListItem>
          ))}
        </div>
      </Hovercard.Content>
    </Hovercard.Root>
  ) : (
    <p className="text-body-md">{label}</p>
  );
}

function ConfiguredStagesCell(props: UseTableCellProps<Job>) {
  const { row } = props;
  const isZeusJob = useIsZeusJob(row?.original?.defaultGuideVersion);

  const configuredCount = row?.original?.configuredStagesCount || 0;
  const unconfiguredCount = row?.original?.unconfiguredStagesCount || 0;

  if (
    row?.original?.setupStatus !==
    JobRoleGuideTemplateSetupStatus.SETUP_COMPLETE
  ) {
    return <span />;
  }

  const hasErrors = !isZeusJob && (!configuredCount || !!unconfiguredCount);
  const configuredLabel = configuredCount || (
    <span className="text-subtle">No stages</span>
  );

  return hasErrors ? (
    <ErrorCell label={configuredLabel} unconfiguredCount={unconfiguredCount} />
  ) : (
    <ValidCell
      label={configuredLabel}
      stages={row?.original?.configuredStages ?? []}
    />
  );
}

export default ConfiguredStagesCell;
